import { FeedDataDto, FeedItemDto, FeedType, GetListPermissionsResponse } from "generated/CoreApi_v1";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { JSX, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from "react-router";
import { FeedContainer, FeedManager } from "views/knowledge/feed/components";
import { FeedBoxPanels } from "views/knowledge/feed/components/FeedBoxPanels";
import { RightPanelBreakpoints } from "views/knowledge/feed/components/FeedContainer";
import { FeedManagerHandle } from "views/knowledge/feed/components/FeedManager";
import { FeedCustomEventProps, subscribe, unsubscribe } from 'views/knowledge/feed/FeedEvents';
import { OutsideRightPanel } from "views/knowledge/publication/pages/components/OutsideRightPanel";
import { IFeedListFilterOptions } from 'views/knowledge/publication/pages/List/FeedListFilter';

interface IProps {
    publication: FeedItemDto;
    feedType: FeedType;
    cardHeader?: JSX.Element;
    rightPanel?: React.ReactElement[];
    filter: IFeedListFilterOptions;
    permissions: GetListPermissionsResponse | null;
    count?: number | null;
    openRightPanel?: boolean;
    marginRightPanel?: number;
    rightPanelBreakpoint?: RightPanelBreakpoints;
    previousPublicationId?: string | null;
    onGobackStart?: () => void;
    onError?: (error: any) => void;
    onCloseRightPanel?: () => void;
    onRefreshData(data?: FeedDataDto, resetFilter?: boolean): void;
}

const FeedList: React.FC<IProps> = (props) => {

    const {
        publication, count, feedType, cardHeader, rightPanel, filter,
        rightPanelBreakpoint, openRightPanel, marginRightPanel, permissions,
        onGobackStart, onCloseRightPanel, onRefreshData, onError
    } = props;

    const api = useCoreApi();
    const navigate = useNavigate();
    const location = useLocation();

    const [prevPub, setPrevPub] = useState<string | null>(location.state?.publicationId ?? null);

    const feedRef = useRef<FeedManagerHandle>(undefined!);

    ///
    const handleNewItem = (e: FeedCustomEventProps) => {
        if (e.detail === publication.publicationId) {
            feedRef.current.reset();
        }
    };

    ///
    const handleReload = (e: FeedCustomEventProps) => {
        if (e.detail == publication.publicationId) {
            feedRef.current.reset();
        }
    };

    ///
    const onGetData = async (startIndex: number, endIndex: number) => {
        var result = await api.v1.knowledgeFeed.getFeedList({
            listPublicationId: publication.publicationId,
            startIndex,
            endIndex,
            searchText: filter.searchText ?? null,
            orderBy: filter.orderBy,
            itemPublicationId: null,
            startPublicationId: location.state?.publicationId
        });
        if (startIndex == 0) onRefreshData(result.data);
        return result.data.items;
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        if (count === null) {
            navigate(document.location.search, { replace: true, state: { filter } });
        }
    }, [count]);

    ///
    useEffect(() => {
        if (prevPub !== location.state?.publicationId) {
            setPrevPub(null);
        }
        feedRef.current?.reset();
    }, [location.state]);

    useEffect(() => {
        subscribe("ListItemAdded", handleNewItem);
        subscribe("ListReload", handleReload);
        return () => {
            unsubscribe("ListItemAdded", handleNewItem);
            unsubscribe("ListReload", handleReload);
        };
    }, []);


    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const Feed = <FeedManager
        feedType={feedType}
        onGetData={onGetData}
        otherInfo={{
            listInfo: {
                publicationId: publication?.publicationId,
                permissions
            }
        }}
        onGobackTop={onGobackStart}
        onError={onError}
        previousPublicationId={prevPub}
        useInfiniteLoading
        ref={feedRef}
    />;

    return (
        <>
            <FeedContainer
                feed={Feed}
                header={cardHeader}
                moreInfo={rightPanel && <FeedBoxPanels>{rightPanel}</FeedBoxPanels>}
                marginRightPanel={marginRightPanel}
                rightPanelBreakpoint={rightPanelBreakpoint}
            />
            {
                rightPanel && openRightPanel &&
                <OutsideRightPanel
                    onClose={onCloseRightPanel != undefined ? onCloseRightPanel : () => { }}
                    content={<>{rightPanel}</>}
                />
            }
        </>
    );
};

function listPropsAreEqual(prevProps: IProps, nextProps: IProps) {
    return prevProps.publication == nextProps.publication &&
        prevProps.filter == nextProps.filter &&
        prevProps.openRightPanel == nextProps.openRightPanel &&
        prevProps.count == nextProps.count;
}

export const FeedListBox = React.memo(FeedList, listPropsAreEqual);
